import { useState, useEffect } from "react";
import axios from "axios";
import { calculateAccountAge } from "./AccountDetails";
import questReputations from "../../utils/repMapping";
import { Link } from "react-router-dom";

export default function PlayerCard({ player }) {
  const [levelData, setLevelData] = useState(null);
  const [showGuildTooltip, setShowGuildTooltip] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [tappedSkill, setTappedSkill] = useState(null);

  useEffect(() => {
    const fetchLevels = async () => {
      try {
        const response = await axios.get("https://api.pixelore.wiki/api/levels");
        setLevelData(response.data);
      } catch (error) {
        console.error("Error fetching level data:", error);
      }
    };
    fetchLevels();
  }, []);

  // Calculate reputation
  const calculateReputation = (playerData) => {
    if (!playerData) return { reputation: "0.00", breakdown: "" };
    const petCount = playerData.pets?.length ?? 0;

    const memberships = playerData.wallets?.memberships || {};
    const quests = playerData.quests || [];

    let discordRep = memberships["Discord"] ? 100 : 0;
    let phoneRep = memberships["phone"] ? 50 : 0;
    let emailRep = memberships["email"] ? 5 : 0;
    let twitterRep = memberships["Twitter"] ? 50 : 0;
    let hasVip = memberships["vip"] || memberships["nftLand-pixels"];
    let vipTier = memberships["vip"]?.count || 0;

    let vipRep = hasVip ? 1500 : 0;

    if (vipTier === 2) vipRep += 500;
    else if (vipTier === 3) vipRep += 1500;
    else if (vipTier === 4) vipRep += 3000;
    let loRep = memberships["nftLand-pixels"] ? Math.min(5000 + (memberships["nftLand-pixels"].count - 1) * 500, 30000) : 0;
    let petRep = petCount > 0 ? Math.min(15 * petCount + 15, 105) : null;
    let guildRep = 0;

    for (const [key] of Object.entries(memberships)) {
      if (key.startsWith("guild-member")) {
        guildRep = 30;
        break;
      }
    }

    let reputation = discordRep + phoneRep + emailRep + twitterRep + vipRep + loRep + guildRep + petRep;
    let breakdown = [
      memberships["Discord"] ? `Discord: ${discordRep} points` : null,
      memberships["phone"] ? `Phone: ${phoneRep} points` : null,
      memberships["email"] ? `Email: ${emailRep} points` : null,
      memberships["Twitter"] ? `Twitter: ${twitterRep} points` : null,
      memberships["vip"] ? `VIP: ${vipRep} points` : memberships["nftLand-pixels"] ? `VIP: ${vipRep} points (For LO)` : null,
      memberships["nftLand-pixels"] ? `NFT Land: ${loRep} points` : null,
      guildRep > 0 ? `Guild Member: ${guildRep} points` : null,
      petRep ? `Pet Owner: ${petRep}` : null,
    ]
      .filter(Boolean)
      .join("<br/>");

    // Account age contribution
    const daysDiff = calculateAccountAge(playerData.createdAt);
    const accountAgeRep = Math.log10(daysDiff * 0.2) * 40;
    reputation += accountAgeRep;

    const questBreakdown = quests
      .map((quest) => {
        const questInfo = questReputations.find((q) => q.id === quest.id);
        if (questInfo && quest.isCompleted && questInfo.points > 0) {
          reputation += questInfo.points;
          return `${questInfo.name} - ${questInfo.points} points`;
        }
        return null;
      })
      .filter(Boolean)
      .join("<br/>");

    return {
      reputation: reputation.toFixed(2),
      breakdown: `<strong>Reputation Breakdown (ESTIMATE):</strong><br/>Account Age: ${accountAgeRep.toFixed(2)} points<br/>${breakdown}${
        questBreakdown ? "<br/>" + questBreakdown : ""
      }`,
    };
  };

  const toggleBreakdown = () => {
    setShowBreakdown((prev) => !prev);
  };

  const reputationData = calculateReputation(player);
  const totalLevel = player?.skills?.reduce((sum, skill) => sum + skill.level, 0) || 0;
  const overallLevel = player?.levels?.overall?.level || 0;

  return (
    <div className="w-full bg-[var(--panel-section-bg)] bg-opacity-80 text-[var(--text-color)] rounded-2xl shadow-lg p-6 border border-[var(--border-color)] transition-transform duration-300 hover:shadow-[var(--border-color)] ">
      {/* Player Image Container */}
      <div className="relative w-24 mx-auto">
        <img
          src={player.currentAvatar?.pieces?.image}
          alt={player.name}
          className="w-24 h-24 rounded-full border-4 border-[var(--border-color)]"
          onError={(e) => {
            const match = e.currentTarget.src.match(/\/ipfs\/(.+)$/);
            const fallback = match ? `https://ipfs.io/ipfs/${match[1]}` : null;
            if (fallback && e.currentTarget.src !== fallback) {
              e.currentTarget.src = fallback;
            }
          }}
        />
        {/* Guild Emblem positioned on the right edge of the avatar */}
        {player.guildEmblem && (
          <div className="absolute -right-2 top-1/2 -translate-y-1/2">
            <div className="relative">
              <Link to={`/lookup/guilds/${player.guildHandle}`} target="_blank" rel="noreferrer">
                <img
                  src={player.guildEmblem}
                  alt="Guild Emblem"
                  className="w-8 h-8 rounded-full transition-transform duration-300 hover:shadow-[var(--border-color)]  hover:scale-150 cursor-pointer"
                  onMouseEnter={() => setShowGuildTooltip(true)}
                  onMouseLeave={() => setShowGuildTooltip(false)}
                />
              </Link>
              {showGuildTooltip && player.guildHandle && (
                <div className="absolute z-10 bg-[var(--level-bars-bg)] text-[var(--text-color)]  px-3 py-2 rounded-lg shadow-lg left-full ml-2 -translate-y-1/2 top-1/2 w-28 text-xs">
                  <p className="font-semibold">Pledge: {player.guildHandle}</p>
                  <p className="text-xs mt-1 font-semibold">Role: {player.guildRole || "Member"}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Player Name */}
      <div className="flex flex-col mt-4">
        <h2 className="text-xl font-bold text-center">{player.name}</h2>
        <h2 className="text-xs text-center">UID: {player._id}</h2>
      </div>

      {/* Stats */}
      <div className="flex flex-col gap-2 mt-3">
        {/* Reputation Score */}
        <div className="flex items-center justify-center gap-1 text-center bg-[var(--level-bars-bg)] text-[var(--level-bars)] text-sm font-semibold py-2 px-4 rounded-lg relative">
          <span>Reputation: {reputationData.reputation.toLocaleString() ?? 0}</span>
          <span className="relative cursor-pointer underline decoration-dotted" onClick={toggleBreakdown}>
            (?)
            {showBreakdown && (
              <div
                className="absolute left-1/2 -translate-x-1/2 -translate-y-1/4 mt-2 w-max max-w-[300px] bg-[var(--panel-sections)] text-[var(--text-color)] border text-xs rounded-lg shadow-lg p-2 z-50"
                dangerouslySetInnerHTML={{ __html: reputationData.breakdown }}
              />
            )}
          </span>
        </div>

        {/* Net Deposit */}
        <div className="text-center bg-[var(--level-bars-bg)] text-[var(--level-bars)] text-sm font-semibold py-2 px-4 rounded-lg relative group">
          Net Deposit:{" "}
          {(
            (player?.wallets?.coinInventory?.find((c) => c.currencyId === "cur_$pixel")?.in ?? 0) -
            (player?.wallets?.coinInventory?.find((c) => c.currencyId === "cur_$pixel")?.out ?? 0)
          ).toLocaleString("en-US") ?? 0}
          {/* Hover tooltip */}
          <div
            className="absolute left-1/2 -translate-x-1/2 -translate-y-full mt-1 w-max max-w-[300px]
                          bg-[var(--panel-sections)] text-[var(--text-color)] border text-xs
                          rounded-lg shadow-lg p-2 z-50 opacity-0 group-hover:opacity-100
                          transition-opacity duration-200 pointer-events-none"
          >
            Deposits: {player?.wallets?.coinInventory?.find((c) => c.currencyId === "cur_$pixel")?.in?.toLocaleString() ?? 0}
            <br />
            Withdraws: {player?.wallets?.coinInventory?.find((c) => c.currencyId === "cur_$pixel")?.out?.toLocaleString() ?? 0}
          </div>
        </div>

        {/* Levels */}
        <div className="flex items-center justify-center gap-4 text-center bg-[var(--level-bars-bg)] text-[var(--level-bars)] text-sm font-semibold py-2 px-4 rounded-lg">
          <span>Total Level: {totalLevel}</span>
          <span className="w-px h-4 bg-[var(--level-bars)] opacity-30" />
          <span>Overall Level: {overallLevel}</span>
        </div>
      </div>

      {/* Skills Section */}
      <div className="mt-3">
        <h3 className="text-lg font-semibold text-[var(--level-bars)]  text-center mb-2">Skills</h3>
        <div className="grid grid-cols-2 gap-2">
          {player.skills.map((skill, index) => {
            const skillLevel = skill.level;
            const exp = skill.exp;
            const nextLevelExp = levelData?.[skillLevel]?.nextLevelExp ?? 1;
            const progress = (exp / nextLevelExp) * 100;

            const isTapped = tappedSkill === index;

            return (
              <div
                key={index}
                className="flex flex-col items-center p-2 bg-[var(--panel-sections)]  border border-[var(--entity-bg-color)] rounded-lg transition-transform duration-300 hover:scale-105 hover:shadow-[0px_0px_10px_2px] hover:shadow-[var(--entity-bg-color)] w-full"
              >
                <img src={skill.icon} alt={skill.name} className="w-6 h-6" />
                <p className="text-xs text-[var(--text-color)] mt-1">{skill.name}</p>
                <p className="text-xs text-[var(--text-color)]">Level {skillLevel}</p>

                <div
                  className="group relative w-full mt-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTappedSkill((prev) => (prev === index ? null : index));
                  }}
                >
                  <div className="w-full bg-[var(--entity-bg-color)] rounded-full h-2 cursor-pointer overflow-hidden">
                    <div className="bg-rose-400 h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                  </div>
                  <div
                    className={`absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-max max-w-[160px] bg-[var(--level-bars-bg)] text-[var(--level-bars)] text-[10px] tabular-nums text-center rounded-lg shadow-lg px-2 py-1 z-10 pointer-events-none transition-opacity duration-150 ${
                      isTapped ? "opacity-100" : "opacity-0 md:group-hover:opacity-100"
                    }`}
                  >
                    {exp?.toLocaleString() || 0} / {nextLevelExp?.toLocaleString() || 0} EXP
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
